// @ts-check
import { computed, unref } from 'vue';

import { documentSummariesSchema } from '@/data/schema/documents';

import { useMutation, useQuery, useQueryClient } from '@/lib/tanstackQuery';
import {
  envelopeKeys,
  mutateEnvelope,
  mutateEnvelopeFields,
  mutateEnvelopeProgress,
  mutateEnvelopeSend,
  mutateEnvelopeSharing,
  mutateEnvelopeWithParties,
  queryEnvelope,
  queryEnvelopeDocuments,
  queryEnvelopeFields,
  queryEnvelopeParties,
  queryEnvelopeSharing,
} from '@/queries/envelopes';

/**
 * Get "draft" envelope
 *
 * @param {string} fingerprint
 */
export function useEnvelope(fingerprint) {
  const query = useQuery({
    queryKey: envelopeKeys.details(fingerprint),
    queryFn: queryEnvelope,
  });

  const isParallelSigning = computed(() => query.data.value?.parallel_signing ?? null);

  return {
    envelope: query.data,
    isParallelSigning,
    refetchEnvelope: query.refetch,
    isPendingEnvelope: query.isPending,
    isFetchingEnvelope: query.isFetching,
    isErrorEnvelope: query.isError,
    errorEnvelope: query.error,
  };
}

/** @param {string} [fingerprint] */
export function useCreateUpdateEnvelope(fingerprint) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: mutateEnvelope(fingerprint),
    onSuccess: (updatedData, variables) => {
      queryClient.setQueryData(
        envelopeKeys.details(updatedData.fingerprint),
        updatedData,
      );

      if (variables.documents?.length) {
        queryClient.invalidateQueries({
          queryKey: envelopeKeys.fields(updatedData.fingerprint),
        });
      }
    },
  });

  return {
    updateEnvelope: mutation.mutateAsync,
    isUpdatingEnvelope: mutation.isPending,
    isUpdateErrorEnvelope: mutation.isError,
    updateErrorEnvelope: mutation.error,
    resetUpdateErrorEnvelope: mutation.reset,
  };
}

/**
 * Get progress information for "draft" envelope
 *
 * @param {import('vue').Ref<string>|string} fingerprint
 */
export function useEnvelopeProgress(fingerprint) {
  const query = useQuery({
    queryKey: envelopeKeys.details(unref(fingerprint) || ''),
    // eslint-disable-next-line no-shadow
    queryFn: (context) => {
      if (!context.queryKey[1]) {
        return Promise.resolve(null);
      }

      return queryEnvelope(context);
    },
  });

  const envelopeSent = computed(() => !!query.data.value?.sent_at && query.data.value.sent_at > 0);
  const envelopeProgress = computed(() => query.data.value?.steps_progress || { hash: undefined, index: undefined });

  return {
    envelopeSent,
    envelopeProgress,
    isPendingEnvelopeProgress: query.isPending,
    isFetchingEnvelopeProgress: query.isFetching,
    isErrorEnvelopeProgress: query.isError,
    errorEnvelopeProgress: query.error,
    refetchEnvelopeProgress: query.refetch,
  };
}

/** @param {string} fingerprint */
export function useUpdateEnvelopeProgress(fingerprint) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: mutateEnvelopeProgress(fingerprint),
    onSuccess: (updatedData) => {
      queryClient.setQueryData(
        envelopeKeys.details(updatedData.fingerprint),
        updatedData,
      );
    },
  });

  return {
    updateEnvelopeProgress: mutation.mutateAsync,
    isUpdatingEnvelopeProgress: mutation.isPending,
    isUpdateErrorEnvelopeProgress: mutation.isError,
    updateErrorEnvelopeProgress: mutation.error,
    resetUpdateErrorEnvelopeProgress: mutation.reset,
  };
}

/**
 * Send an envelope
 *
 * @param {string} fingerprint
 */
export function useEnvelopeSend(fingerprint) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: mutateEnvelopeSend(fingerprint),
    onSuccess: (updatedData) => {
      queryClient.setQueryData(
        envelopeKeys.details(updatedData.fingerprint),
        updatedData,
      );
    },
  });

  return {
    sendEnvelope: mutation.mutateAsync,
    isSendingEnvelope: mutation.isPending,
    isSendingErrorEnvelope: mutation.isError,
    sendErrorEnvelope: mutation.error,
    resetSendErrorEnvelope: mutation.reset,
  };
}

/**
 * Get "draft" envelope documents
 *
 * @param {string} fingerprint
 */
export function useEnvelopeDocuments(fingerprint) {
  const query = useQuery({
    queryKey: envelopeKeys.documents(fingerprint),
    queryFn: queryEnvelopeDocuments,
  });

  return {
    documents: query.data,
    refetchDocuments: query.refetch,
    isPendingDocuments: query.isPending,
    isFetchingDocuments: query.isFetching,
    isErrorDocuments: query.isError,
    errorDocuments: query.error,
  };
}

/**
 * Get "draft" envelope parties
 *
 * @param {string} fingerprint
 */
export function useEnvelopeParties(fingerprint) {
  const query = useQuery({
    queryKey: envelopeKeys.parties(fingerprint),
    queryFn: queryEnvelopeParties,
  });

  return {
    parties: query.data,
    refetchParties: query.refetch,
    isPendingParties: query.isPending,
    isFetchingParties: query.isFetching,
    isErrorParties: query.isError,
    errorParties: query.error,
  };
}

/**
 * Update "draft" envelope with parties
 *
 * @param {string} fingerprint
 */
export function useUpdateEnvelopeWithParties(fingerprint) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: mutateEnvelopeWithParties(fingerprint),
    onSuccess: ([updatedEnvelope, updatedParties]) => {
      queryClient.setQueryData(
        envelopeKeys.details(fingerprint),
        updatedEnvelope,
      );
      queryClient.setQueryData(
        envelopeKeys.parties(fingerprint),
        updatedParties,
      );
    },
  });

  return {
    updateEnvelopeWithParties: mutation.mutateAsync,
    isUpdatingEnvelopeWithParties: mutation.isPending,
    isUpdateErrorEnvelopeWithParties: mutation.isError,
    updateErrorEnvelopeWithParties: mutation.error,
    resetUpdateErrorEnvelopeWithParties: mutation.reset,
  };
}

/**
 * Get "draft" envelope document summaries
 *
 * @param {string} fingerprint
 */
export function useEnvelopeDocumentSummaries(fingerprint) {
  const query = useQuery({
    queryKey: envelopeKeys.documents(fingerprint),
    queryFn: queryEnvelopeDocuments,
  });

  const documentSummaries = computed(() =>
    (!query.isPending.value && !query.isError.value
      ? documentSummariesSchema.parse(query.data.value)
      : []));

  return {
    documentSummaries,
    refetchDocumentSummaries: query.refetch,
    isPendingDocumentSummaries: query.isPending,
    isFetchingDocumentSummaries: query.isFetching,
    isErrorDocumentSummaries: query.isError,
    errorDocumentSummaries: query.error,
  };
}

/**
 * Get "draft" envelope sharing
 *
 * @param {string} fingerprint
 */
export function useEnvelopeSharing(fingerprint) {
  const query = useQuery({
    queryKey: envelopeKeys.sharing(fingerprint),
    queryFn: queryEnvelopeSharing,
  });

  return {
    sharing: query.data,
    refetchSharing: query.refetch,
    isPendingSharing: query.isPending,
    isFetchingSharing: query.isFetching,
    isErrorSharing: query.isError,
    errorSharing: query.error,
  };
}

/**
 * Set "draft" envelope sharing
 *
 * @param {string} fingerprint
 */
export function useUpdateEnvelopeSharing(fingerprint) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: mutateEnvelopeSharing(fingerprint),
    onSuccess: (updatedData) => {
      queryClient.setQueryData(envelopeKeys.sharing(fingerprint), updatedData);
    },
  });

  return {
    updateSharing: mutation.mutateAsync,
    isUpdatingSharing: mutation.isPending,
    isUpdateErrorSharing: mutation.isError,
    updateErrorSharing: mutation.error,
    resetUpdateErrorSharing: mutation.reset,
  };
}

export function useIsFetchingEnvelopes() {
  const queryClient = useQueryClient();

  return computed(() => queryClient.isFetching({ queryKey: envelopeKeys.all }));
}

export function useClearEnvelopesCache() {
  const queryClient = useQueryClient();

  return () => queryClient.removeQueries({ queryKey: envelopeKeys.all });
}

/**
 * Get the fields for an envelope
 *
 * @param {string} fingerprint
 */
export function useEnvelopeFields(fingerprint) {
  const {
 isPending, isFetching, isError, data, error, refetch,
} = useQuery({
    queryKey: envelopeKeys.fields(fingerprint),
    queryFn: queryEnvelopeFields,
  });

  return {
    envelopePartyFields: data,
    refetchFields: refetch,
    isPendingFields: isPending,
    isFetchingFields: isFetching,
    isErrorFields: isError,
    errorFields: error,
  };
}

/**
 * Update the envelope fields
 *
 * @param {string} fingerprint
 */
export function useUpdateEnvelopeFields(fingerprint) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: mutateEnvelopeFields(fingerprint),
    onSuccess: (updatedData) => {
      queryClient.setQueryData(envelopeKeys.fields(fingerprint), updatedData);
    },
  });

  return {
    updateEnvelopeFields: mutation.mutateAsync,
    isUpdatingEnvelopeFields: mutation.isPending,
    isUpdateErrorEnvelopeFields: mutation.isError,
    updateErrorEnvelopeFields: mutation.error,
    resetUpdateErrorEnvelopeFields: mutation.reset,
  };
}
